import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';

export interface TimeSeriesSeries {
  key: string;
  label: string;
  color: string;
  stackId?: string;
  fillOpacity?: number;
  strokeWidth?: number;
  /** Optional value formatter for the tooltip (e.g. percentage). */
  valueFormatter?: (value: number) => string;
}

interface TimeSeriesChartProps {
  title: string;
  description: string;
  data: any[];
  series: TimeSeriesSeries[];
  variant?: 'line' | 'area';
  height?: number;
  className?: string;
  yDomain?: [number | string, number | string];
  showLegend?: boolean;
  /** Optional max-value override; defaults to true. */
  showCurrentYearLine?: boolean;
  /** Render in card content (e.g. extra footer note). */
  footer?: ReactNode;
}

export default function TimeSeriesChart({
  title,
  description,
  data,
  series,
  variant = 'line',
  height = 280,
  className,
  yDomain,
  showLegend,
  showCurrentYearLine = true,
  footer,
}: TimeSeriesChartProps) {
  const { t } = useTranslation('dashboard');
  const currentYear = new Date().getFullYear();

  const chartConfig = useMemo<ChartConfig>(() => {
    const cfg: ChartConfig = {};
    for (const s of series) {
      cfg[s.key] = { label: s.label, color: s.color };
    }
    return cfg;
  }, [series]);

  const showCurrent =
    showCurrentYearLine &&
    data.length > 0 &&
    Number(data[0]?.year) <= currentYear &&
    Number(data[data.length - 1]?.year) >= currentYear;

  const tooltipFormatter = (value: any, _name: any, item: any) => {
    const s = series.find((x) => x.key === item?.dataKey);
    const formatted = s?.valueFormatter
      ? s.valueFormatter(Number(value))
      : Number(value).toLocaleString();
    return (
      <div className="flex w-full items-center justify-between gap-3">
        <span className="flex items-center gap-2">
          <span
            className="size-2 shrink-0 rounded-[2px]"
            style={{ backgroundColor: s?.color }}
          />
          <span className="text-muted-foreground">{s?.label || item?.name}</span>
        </span>
        <span className="font-mono tabular-nums text-foreground">{formatted}</span>
      </div>
    );
  };

  const useLegend = showLegend ?? series.length > 1;

  return (
    <Card className={`border-ctu-border shadow-none ${className ?? ''}`}>
      <CardContent className="flex flex-col gap-3 px-6 pt-5 pb-4">
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
            {title}
          </span>
          <span className="text-xs text-ctu-text-muted leading-snug">
            {description}
          </span>
        </div>

        {data.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-sm text-ctu-text-muted">
            —
          </div>
        ) : (
          <ChartContainer
            config={chartConfig}
            className="aspect-auto w-full"
            style={{ height }}
          >
            {variant === 'area' ? (
              <AreaChart
                data={data}
                margin={{ top: 16, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis
                  dataKey="year"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={12}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={42}
                  domain={yDomain}
                  allowDecimals={false}
                  tickMargin={4}
                />
                <ChartTooltip
                  cursor={{ stroke: 'hsl(var(--border))', strokeDasharray: '3 3' }}
                  content={
                    <ChartTooltipContent
                      hideIndicator
                      labelFormatter={(value) => String(value)}
                      formatter={tooltipFormatter}
                    />
                  }
                />
                {useLegend && (
                  <ChartLegend
                    content={<ChartLegendContent />}
                    verticalAlign="bottom"
                    height={28}
                  />
                )}
                {series.map((s) => (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    stackId={s.stackId}
                    stroke={`var(--color-${s.key})`}
                    fill={`var(--color-${s.key})`}
                    fillOpacity={s.fillOpacity ?? 0.28}
                    strokeWidth={s.strokeWidth ?? 2}
                  />
                ))}
                {showCurrent && (
                  <ReferenceLine
                    x={currentYear}
                    stroke="hsl(var(--foreground))"
                    strokeDasharray="4 4"
                    strokeOpacity={0.45}
                    label={{
                      value: t('chart.currentYear'),
                      position: 'top',
                      fill: 'hsl(var(--foreground))',
                      fillOpacity: 0.6,
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  />
                )}
              </AreaChart>
            ) : (
              <LineChart
                data={data}
                margin={{ top: 16, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis
                  dataKey="year"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={12}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={42}
                  domain={yDomain}
                  allowDecimals={false}
                  tickMargin={4}
                />
                <ChartTooltip
                  cursor={{ stroke: 'hsl(var(--border))', strokeDasharray: '3 3' }}
                  content={
                    <ChartTooltipContent
                      hideIndicator
                      labelFormatter={(value) => String(value)}
                      formatter={tooltipFormatter}
                    />
                  }
                />
                {useLegend && (
                  <ChartLegend
                    content={<ChartLegendContent />}
                    verticalAlign="bottom"
                    height={28}
                  />
                )}
                {series.map((s) => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    stroke={`var(--color-${s.key})`}
                    strokeWidth={s.strokeWidth ?? 2.25}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                ))}
                {showCurrent && (
                  <ReferenceLine
                    x={currentYear}
                    stroke="hsl(var(--foreground))"
                    strokeDasharray="4 4"
                    strokeOpacity={0.45}
                    label={{
                      value: t('chart.currentYear'),
                      position: 'top',
                      fill: 'hsl(var(--foreground))',
                      fillOpacity: 0.6,
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  />
                )}
              </LineChart>
            )}
          </ChartContainer>
        )}

        {footer}
      </CardContent>
    </Card>
  );
}
